import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";

const VideoHandler = ({ imageUrl, mediaUrl, isVideoUse = true }) => {
  const [state, setState] = useState({
    isLoaded: false,
    isPlaying: false,
    showPlayButton: false,
    mediaError: false,
    isInView: false, // 뷰포트 진입 여부 (이전: isPageLoaded = window.load 기준)
  });

  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const updateState = useCallback((newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  }, []);
  const handlePlay = useCallback(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement
        .play()
        .then(() => {
          updateState({ isPlaying: true, showPlayButton: false });
        })
        .catch((error) => {
          console.error("Play failed:", error);
          updateState({ isPlaying: false, showPlayButton: true });
        });
    }
  }, [updateState, videoRef]);

  const handleCanPlayThrough = useCallback(() => {
    updateState({ isLoaded: true });
    handlePlay();
  }, [updateState, handlePlay]);

  const handlePause = useCallback(() => {
    updateState({ isPlaying: false, showPlayButton: true });
  }, [updateState]);

  const handleMediaError = useCallback(() => {
    console.error("Media failed to load");
    updateState({ mediaError: true, showPlayButton: false });
  }, [updateState]);

  // 뷰포트 진입 감지 — 화면(또는 200px 이내)에 들어올 때만 영상 로드.
  // 이전에는 window.load 직후 모든 영상을 즉시 다운로드해 폴드 밖 영상까지
  // 초기 대역폭을 잠식했음. IntersectionObserver로 폴드 밖 영상을 지연 로드한다.
  useEffect(() => {
    if (!isVideoUse) return;

    const containerCurrent = containerRef.current;
    if (!containerCurrent) return;

    // IntersectionObserver 미지원 환경 fallback — 즉시 로드
    if (typeof IntersectionObserver === "undefined") {
      updateState({ isInView: true });
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          updateState({ isInView: true });
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0,
        rootMargin: "200px", // 뷰포트 경계 200px 바깥에서 미리 트리거
      }
    );

    observer.observe(containerCurrent);
    return () => observer.unobserve(containerCurrent);
  }, [isVideoUse, updateState]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && state.isInView) {
      videoElement.addEventListener("canplaythrough", handleCanPlayThrough);
      videoElement.addEventListener("play", handlePlay);
      videoElement.addEventListener("pause", handlePause);
      videoElement.addEventListener("error", handleMediaError);

      // 뷰포트 진입 시점에 로드 및 자동 재생 시도
      videoElement.load();

      return () => {
        videoElement.removeEventListener(
          "canplaythrough",
          handleCanPlayThrough
        );
        videoElement.removeEventListener("play", handlePlay);
        videoElement.removeEventListener("pause", handlePause);
        videoElement.removeEventListener("error", handleMediaError);
      };
    }
  }, [
    state.isInView,
    handleCanPlayThrough,
    handlePlay,
    handlePause,
    handleMediaError,
  ]);

  const renderMedia = useCallback(() => {
    if (state.mediaError) {
      return <Image src={imageUrl} alt="Fallback image" />;
    }

    if (!state.isInView) {
      return <Image src={imageUrl} alt="Placeholder image" />;
    }

    return (
      <>
        <Video
          ref={videoRef}
          loop
          muted
          playsInline
          preload="auto"
          onError={handleMediaError}
          poster={imageUrl}
        >
          <source src={mediaUrl} type="video/mp4" />
        </Video>
        {state.isLoaded && !state.isPlaying && state.showPlayButton && (
          <PlayButton onClick={handlePlay} aria-label="Play video">
            ▶
          </PlayButton>
        )}
      </>
    );
  }, [state, imageUrl, mediaUrl, handleMediaError, handlePlay]);

  // TODO: IR 이후 주석 풀기
  return (
    <Container ref={containerRef}>
      {isVideoUse ? (
        <>
          {renderMedia()}
          {!state.isLoaded && state.isInView && !state.mediaError && (
            <LoadingOverlay>
              <Image src={imageUrl} alt="Video placeholder" />
            </LoadingOverlay>
          )}
        </>
      ) : (
        <Image src={imageUrl} alt="Video placeholder" />
      )}
    </Container>
  );
};

export default React.memo(VideoHandler);

const Container = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
`;

const Video = styled.video`
  width: 100%;
  height: auto;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;
